import { attachAuthInterceptors } from '@blattnercompany/react-auth'
import axios, { type AxiosInstance, type AxiosResponse } from 'axios'

import { apiScopes } from '@/configs/authConfig'
import { env } from '@/configs/env'
import { logger } from '@/utils/logger'

const log = logger.scoped('apiClient')

const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Auth from @blattnercompany/react-auth: bearer-token injection plus the one
// forced-refresh retry on 401, with a redirect when Entra needs interaction
// (claims forwarded for step-up MFA). Don't add an app-owned auth retry on
// top -- stacked retries multiply.
//
// Requests fail closed: with no signed-in account or no token, the request is
// rejected before it is sent. Anything that must work signed-out needs its
// own axios client. The interceptor reads the MSAL instance <AuthProvider>
// registered in main.tsx, so the first request has to happen under that
// provider -- automatic in the component tree. Bare axios.* calls bypass all
// of this and go out without a token.
attachAuthInterceptors(apiClient, {
  scopes: apiScopes,
  // Notification only; the request still rejects. Never log the token itself.
  onError: (error) => log.warn('Silent token acquisition failed', { error }),
})

// Registered after the auth interceptors, so this sees final outcomes only: a
// 401 here has already had its refresh-and-retry.
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: unknown) => {
    // A non-axios error here is the auth interceptor rejecting before the
    // request went out; its onError above already logged it, so don't log a
    // second, misleading "network" warning.
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error)
    }
    if (error.response?.status === 401) {
      log.warn(`Unauthorized request [path=${error.config?.url ?? 'unknown'}]`)
    } else if (!error.response) {
      // No response at all -- network, CORS or timeout. Without this branch
      // those failures are invisible.
      log.warn(
        `Request failed before reaching the server [path=${error.config?.url ?? 'unknown'}]`,
        { error },
      )
    }
    return Promise.reject(error)
  },
)

export default apiClient
